<template>
  <div class="Playlists">
    <MainPageHeader v-if="!isAndroidContext"/>
    <div :class="{'android-content': isAndroidContext}" class="content">
      <h1 v-if="!isAndroidContext" class="font-size-big">Moje playlisty</h1>
      <SmallButton border-color="primary"
                   class="add-new-button" @click="addNewPlaylist">
        Dodaj nową
      </SmallButton>
      <div class="playlist-search">
        <PlaylistSearch
          :value="query"
          :isInputEmpty="isInputEmpty"
          :isLoading="isLoading"
          @input="handleSearchInput"
          @search="handleSearch"
        />
      </div>
      <LoadingPlaylistCards v-if="!arePlaylistsLoaded" :amount="4"/>
      <div class="playlist-list-wrapper">
        <div v-for="(playlist) in playlists" :key="playlist.key"
             :class="{ disabled: !arePlaylistsLoaded, visible: arePlaylistsLoaded }"
             :playlist="playlist"
             class="playlist-list">
          <button class="playlist-info"
                  @click="editPlaylist(playlist.id)">
            <div class="name font-size-middle">
            <span :class="{ long: playlist.name.length > 20, short: playlist.name.length < 20}">
              {{ playlist.name }}</span>
            </div>
            <div class="lyricsCount">
              <h2 class="font-size-small">Ilość piosenek w playliście: {{ playlist.playlistItems.length }}
                ({{ playlist.minutes }}min {{ playlist.seconds }}s)</h2>
            </div>
            <p class="font-size-small">Kliknij aby edytować lub usunąć</p>
          </button>
          <div class="border-background">
          </div>
          <div class="border-background-second">
          </div>
        </div>
        <div class="infinite-scroll" ref="infiniteScroll"></div>
      </div>
      <div v-if="playlists != null">
        <p class="playlists-counter font-size-middle">{{ visiblePlaylistsCounter }}</p>
      </div>
      <div v-if="isLoadingNextPlaylistsPage" class="loader"></div>
      <p v-if='playlistsNotFound' class="font-size-middle">
        Kliknij przycisk <a class="yellow-link" @click="addNewPlaylist">Dodaj własną</a>, aby utworzyć nową playlistę.
      </p>
    </div>
    <Footer v-if="!isAndroidContext"/>
  </div>
</template>

<script>
import MainPageHeader from '../components/MainPageHeader.vue'
import { getLyricsService } from '@/services/lyricsService'
import { getAuthService } from '../services/authService'
import SmallButton from '@/components/input/SmallButton'
import LoadingPlaylistCards from '../components/LoadingPlaylistCards'
import '../../node_modules/placeholder-loading/src/scss/placeholder-loading.scss'
import Footer from '@/components/Footer'
import { getAndroidContext } from '@/services/sessionProps'
import PlaylistSearch from '@/components/PlaylistSearch'
import { createCancellable } from '@/helpers/asyncHelper'

export default {
  name: 'Playlist-panel',
  components: {
    Footer,
    SmallButton,
    MainPageHeader,
    LoadingPlaylistCards,
    PlaylistSearch
  },
  data () {
    return {
      playlistId: null,
      playlistsNotFound: false,
      playlists: this.getPlaylistData(1),
      arePlaylistsLoaded: false,
      isAndroidContext: false,
      lastPage: null,
      totalItems: null,
      nextPage: null,
      observer: null,
      isLoadingNextPlaylistsPage: false,
      initialLoading: true,
      searching: false,
      query: '',
      isInputEmpty: false,
      isLoading: false
    }
  },
  mounted () {
    window.scrollTo(0,0);
    (async () => {
      this.isAndroidContext = getAndroidContext().isAndroidContext()
      const [
        loggedInEmail,
      ] = await Promise.all([
        getAuthService().getLoggedInEmail(),
      ])

      this.loggedInEmail = loggedInEmail
      if (this.loggedInEmail == null) {
        this.$router.replace({
          name: 'logInPage',
          params: { fromPage: 'playlists' }
        })
      } else {
        this.observer = new IntersectionObserver(this.onElementObserved, {
          threshold: 0.0,
        })
      }
    })()
  },
  methods: {
    handleSearchInput (query) {
      this.query = query
      this.isInputEmpty = true
    },
    handleSearch (query) {
      this.searching = true
      this.isLoading = true

      if (this.playlistFetchAction) {
        this.playlistFetchAction.cancel()
      }

      this.playlistFetchAction = createCancellable(() => getLyricsService().searchPlaylist(query))

      if (query.length !== 0) {
        this.playlistFetchAction().then((playlists) => {
          this.playlists = playlists['hydra:member']
          this.searching = false
          this.isLoading = false
          this.lyricsFetchAction = null
          this.playlistsNotFound = false
          if (this.playlists.length === 0) {
            this.playlistsNotFound = true
          }

          this.playlists.forEach((element) => {
            let minutes = 0
            let seconds = 0
            element.playlistItems.forEach((item) => {
              minutes = minutes + parseInt(item.bandLyric.duration.substring(14,16))
              seconds = seconds + parseInt(item.bandLyric.duration.substring(17,19))
            })
            if(seconds > 59){
              minutes = minutes + Math.floor(seconds / 60)
              seconds = seconds % 60
            }
            element.minutes = minutes.toString()
            element.seconds = seconds.toString()
          })
        })
      } else {
        this.searching = false
        this.isLoading = false
        this.playlists = this.getPlaylistData(1)
        this.playlistFetchAction = null
        this.playlistsNotFound = false
        this.isInputEmpty = false
        this.observeScrollElement()
      }
    },
    onElementObserved (entries) {
      if (entries[0].isIntersecting === true && this.nextPage <= this.lastPage) {
        this.getPlaylistData(this.nextPage)
      }
    },
    observeScrollElement () {
      if (!this.playlistsNotFound && this.playlists != null) {
        setTimeout(() => {
          if (this.$refs.infiniteScroll != null) {
            this.observer.observe(this.$refs.infiniteScroll)
          }
        }, 1000)
      }
    },
    async getPlaylistData (page) {
      if (page === 1) {
        ({ playlists: this.playlists, lastPage: this.lastPage, totalItems: this.totalItems }
            = await getLyricsService().getPlaylists(page))
      } else {
        this.isLoadingNextPlaylistsPage = true
        let nextPagePlaylists
        ({ playlists: nextPagePlaylists, lastPage: this.lastPage, totalItems: this.totalItems }
            = await getLyricsService().getPlaylists(page))
        this.playlists = this.playlists.concat(nextPagePlaylists)
        this.isLoadingNextPlaylistsPage = false
        this.initialLoading = false
      }

      this.arePlaylistsLoaded = true
      this.nextPage = this.nextPage <= this.lastPage ? ++page : this.lastPage

      if (this.playlists.length === 0) {
        this.playlistsNotFound = true
      } else if (this.initialLoading) {
        this.observeScrollElement()
      }
      this.playlists.forEach((element) => {
        let minutes = 0
        let seconds = 0
        element.playlistItems.forEach((item) => {
          minutes = minutes + parseInt(item.bandLyric.duration.substring(14,16))
          seconds = seconds + parseInt(item.bandLyric.duration.substring(17,19))
        })
        if(seconds > 59){
          minutes = minutes + Math.floor(seconds / 60)
          seconds = seconds % 60
        }
        element.minutes = minutes.toString()
        element.seconds = seconds.toString()
      })
    },
    addNewPlaylist () {
      this.$router.push({ name: 'playlistAddition' })
    },
    editPlaylist (id) {
      this.$router.push({ name: 'PlaylistEditionPage', params: { id: id } })
    },
  },
  computed: {
    visiblePlaylistsCounter () {
      if (this.playlists?.length > 0) {
        return `Wczytano ${this.playlists.length}
        z ${this.totalItems} playlist`
      } else {
        return ``
      }
    },
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";

.Playlists {
  position: relative;
  padding-bottom: 20px;
}

.content {
  box-sizing: content-box;
  max-width: 100%;
  margin: 90px auto 0;
  padding: 0 40px;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 380px);
  @media only screen and (max-width: 600px) {
    padding: 0 20px;
  }

  .yellow-link {
    color: $primary;
    text-decoration: none;
    cursor: pointer;
  }

  .disabled {
    display: none
  }

  .visible {
    display: block;
  }

  .playlist-list-wrapper {
    position: relative;
    max-width: 450px;
    margin: auto;

    .infinite-scroll {
      pointer-events: none;
      z-index: -100;
      height: 400px;
      width: 100%;
      position: absolute;
      bottom: 0;
    }
  }

  .playlist-list {
    button {
      padding: 1px 0;
      height: 100%;
    }

    margin: 0 auto 40px;
    max-width: 450px;
    border: 2px solid $primary;
    border-radius: 3px;
    height: 70px;
    @media only screen and (max-width: 600px) {
      max-width: 400px;
    }
    @media only screen and (max-width: 400px) {
      max-width: 350px;
    }

    .playlist-info {
      overflow: hidden;
      padding-left: 20px;
      background-color: transparent;
      border: none;
      cursor: pointer;
      display: grid;
      grid-template: "name nav" 1fr
                     "count nav" 1fr
                      /2fr 1fr;
      @media only screen and (max-width: 400px) {
        grid-template: "name nav" 1fr
                     "count nav" 1fr
                      /2.5fr 1fr;
      }

      .name {
        margin: 10px 30px auto 20px;
        position: relative;
        white-space: nowrap;
        overflow: hidden;
        text-align: left;
        padding-top: 5px;
        grid-column-start: 1;
        color: $primary;
        grid-area: name;
        @media only screen and (min-width: 769px) {
          .long {
            display: inline-block;
            animation: slide 10s linear infinite;
          }
        }

        @media only screen and (max-width: $phone-max-width) {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        @media only screen and (max-width: 600px) {
          margin-left: 10px;
        }
        @media only screen and (max-width: 400px) {
          margin-left: 6px;
        }
        @keyframes slide {
          0%, 10% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-100%);
          }
        }
      }

      .lyricsCount {
        grid-column-start: 1;
        grid-area: count;

        h2 {
          text-align: left;
          padding-top: 1px;
          padding-left: 20px;
          color: $grey-light;
          margin-bottom: 10px;
          @media only screen and (max-width: 600px) {
            padding-left: 10px;
          }
          @media only screen and (max-width: 400px) {
            padding-left: 6px;
            margin-top: 0;
            padding-right: 8px;
            padding-top: 10px;
          }
        }
      }

      p {
        padding-right: 20px;
        grid-column-start: 2;
        grid-area: nav;
        position: relative;
        margin-bottom: auto;
        margin-top: auto;
        color: $dark-text;
        @media only screen and (max-width: 600px) {
          padding-right: 10px;
        }
        @media only screen and (max-width: 400px) {
          padding-right: 6px;
        }
      }
    }
  }

  .playlists-counter {
    text-align: center;
    color: $white;
    margin-bottom: 20px;
  }

  .loader {
    width: 20px;
    margin: 1px;
    font-size: 10px;
    position: relative;
    margin-left: auto;
    margin-right: auto;
    margin-top: 20px;
    margin-bottom: 20px;
    text-indent: -9999em;
    border-top: 0.5em solid $primary;
    border-right: 0.5em solid $primary;
    border-bottom: 0.5em solid $primary;
    border-left: 0.5em solid #ffffff;
    -webkit-animation: load8 1.1s infinite linear;
    animation: load8 0.8s infinite linear;
  }

  .loader,
  .loader:after {
    border-radius: 100%;
    width: 5em;
    height: 5em;
  }

  @-webkit-keyframes load8 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
  @keyframes load8 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }

  .border-background {
    border-bottom: 4px ridge $dark-text;
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;
    max-width: 435px;
    margin: 3px auto 0;
    @media only screen and (max-width: 600px) {
      max-width: 385px;
    }
    @media only screen and (max-width: 400px) {
      max-width: 300px;
    }
  }

  .border-background-second {
    border-bottom: 4px ridge $dark-text;
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;
    max-width: 410px;
    margin: 0 auto 40px;
    @media only screen and (max-width: 600px) {
      max-width: 370px;
    }
    @media only screen and (max-width: 400px) {
      max-width: 260px;
    }
  }

  h1 {
    margin: 0 auto;
    margin-bottom: 30px;
    padding: 15px;
    color: $primary;
    text-align: center;
    border-bottom: 2px solid #fefefe;
    width: 100%;

    @media screen and (max-width: $phone-max-width) {
      padding-top: 15px;
      margin-bottom: 20px;
    }
  }

  .add-new-button {
    display: block;
    margin: 0 auto;
    max-width: 120px;
    width: 100%;
    min-width: 130px;
    border-radius: 5px;
  }

  p {
    margin: 0 auto;
    margin-top: 20px;
    color: #999;
    text-align: center;
  }

  //mobile style
  @media (max-width: $phone-max-width) {
    .main-page-header {
      display: none;
    }

    .content {
      position: static;
      margin: 50px 10px;
    }
  }

}

.android-content {
  margin-top: 30px;
}

.playlist-search {
    display: flex;
    flex-direction: column;
    margin: auto;
    max-width: 380px;
    margin-top: 40px;
}
</style>
